import "./EditAddress.scss";
import { useState, useEffect } from "react";
import addressData from "../../../../../../../../Data/ph-address-2019v2.json";
import { useAppContext } from "../../../../../../../../Components/AppProvider";

function EditAddress({ onClose, user_id, editAddress, onAddressEdited }) {
  const { hasNewAddress, setHasNewAddress } = useAppContext();

  // Focus tracking
  const [isRegionFocused, setIsRegionFocused] = useState(false);
  const [isProvinceFocused, setIsProvinceFocused] = useState(false);
  const [isCityFocused, setIsCityFocused] = useState(false);
  const [isBrgyFocused, setIsBrgyFocused] = useState(false);

  // Dropdown highlight tracking
  const [highlightRegion, setHighlightRegion] = useState(0);
  const [highlightProvince, setHighlightProvince] = useState(0);
  const [highlightCity, setHighlightCity] = useState(0);
  const [highlightBrgy, setHighlightBrgy] = useState(0);

  // Field values
  const [regionValue, setRegionValue] = useState("");
  const [provinceValue, setProvinceValue] = useState("");
  const [cityValue, setCityValue] = useState("");
  const [brgyValue, setBrgyValue] = useState("");
  const [fullName, setFullName] = useState("");
  const [contact, setContact] = useState("");
  const [street, setStreet] = useState("");
  const [landmark, setLandmark] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Load existing address
  useEffect(() => {
    if (!editAddress) return;
    setFullName(editAddress.CA_Fullname || "");
    setContact(editAddress.CA_Contactnumber || "");
    setRegionValue(editAddress.CA_Region || "");
    setProvinceValue(editAddress.CA_DistrictProvince || "");
    setCityValue(editAddress.CA_CityMunicipality || "");
    setBrgyValue(editAddress.CA_Barangay || "");
    setStreet(editAddress.CA_StreetAddress || "");
    setLandmark(editAddress.CA_Landmark || "");
  }, [editAddress]);

  // --- Address hierarchy ---
  const regionsArray = Object.entries(addressData).map(([code, data]) => ({
    code,
    name: data.region_name,
    provinces: data.province_list,
  }));

  const filteredRegions = regionsArray.filter((r) =>
    r.name.toLowerCase().includes(regionValue.toLowerCase())
  );
  const matchedRegion = regionsArray.find(
    (r) => r.name.toLowerCase() === regionValue.trim().toLowerCase()
  );

  const filteredProvinces =
    matchedRegion &&
    Object.keys(matchedRegion.provinces || {}).filter((p) =>
      p.toLowerCase().includes(provinceValue.toLowerCase())
    );

  const matchedProvinces =
    matchedRegion &&
    Object.keys(matchedRegion.provinces || {}).find(
      (p) => p.toLowerCase() === provinceValue.trim().toLowerCase()
    );

  const filteredCities =
    matchedProvinces &&
    Object.keys(
      matchedRegion.provinces[matchedProvinces]?.municipality_list || {}
    ).filter((c) => c.toLowerCase().includes(cityValue.toLowerCase()));

  const matchedCities =
    matchedProvinces &&
    Object.keys(
      matchedRegion.provinces[matchedProvinces]?.municipality_list || {}
    ).find((c) => c.toLowerCase() === cityValue.trim().toLowerCase());

  const filteredBarangays =
    matchedCities &&
    matchedRegion.provinces[matchedProvinces]?.municipality_list[
      matchedCities
    ]?.barangay_list?.filter((b) =>
      b.toLowerCase().includes(brgyValue.toLowerCase())
    );

  // --- Auto-highlight first result when typing ---
  useEffect(() => {
    if (isRegionFocused && filteredRegions.length > 0) setHighlightRegion(0);
  }, [regionValue]);
  useEffect(() => {
    if (isProvinceFocused && filteredProvinces?.length > 0)
      setHighlightProvince(0);
  }, [provinceValue]);
  useEffect(() => {
    if (isCityFocused && filteredCities?.length > 0) setHighlightCity(0);
  }, [cityValue]);
  useEffect(() => {
    if (isBrgyFocused && filteredBarangays?.length > 0) setHighlightBrgy(0);
  }, [brgyValue]);

  // --- Keyboard navigation handler ---
  // --- Keyboard navigation handler (improved same as NewAddress) ---
  const handleKeyDown = (
    e,
    list,
    setValue,
    highlight,
    setHighlight,
    setFocus
  ) => {
    if (!Array.isArray(list) || list.length === 0) return;

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const newIndex =
        e.key === "ArrowDown"
          ? (highlight + 1) % list.length
          : (highlight - 1 + list.length) % list.length;
      setHighlight(newIndex);

      // Auto-scroll highlighted item into view
      setTimeout(() => {
        document
          .querySelector(".emc-keyword-result .highlighted")
          ?.scrollIntoView({ block: "nearest" });
      }, 10);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const selected = list[highlight];
      if (selected) {
        setValue(selected);
        setFocus(false);

        // Move to next input on Tab
        if (e.key === "Tab" || e.key === "Enter") {
          setTimeout(() => {
            const inputs = Array.from(
              document.querySelectorAll("input[type='text']")
            );
            const i = inputs.indexOf(document.activeElement);
            if (i !== -1 && i < inputs.length - 1) {
              inputs[i + 1].focus();
            }
          }, 30);
        }
      }
    } else if (e.key === "Escape") {
      setFocus(false);
    }
  };

  // --- Validation ---
  const validateForm = () => {
    const newErrors = {};
    if (!fullName.trim()) newErrors.fullName = "Full Name is required";
    if (!contact.trim()) newErrors.contact = "Contact is required";
    if (!regionValue.trim()) newErrors.region = "Region is required";
    if (!provinceValue.trim()) newErrors.province = "Province is required";
    if (!cityValue.trim()) newErrors.city = "City/Municipality is required";
    if (!brgyValue.trim()) newErrors.brgy = "Barangay is required";
    if (!street.trim()) newErrors.street = "Street/House No. is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --- Save ---
  const handleSave = async () => {
    setSaving(true);
    if (validateForm()) {
      const json = JSON.stringify({
        caId: editAddress.CA_ID || "",
        fullName,
        contact,
        region: regionValue,
        province: provinceValue,
        city: cityValue,
        barangay: brgyValue,
        street,
        landmark,
      });
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}api/address/update-address`,
          {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: json,
          }
        );
        const data = await res.json();
        if (!res.ok) {
          alert(`Error: ${data.error}`);
          setSaving(false);
          return;
        }
        setHasNewAddress((p) => !p);
        onAddressEdited();
        onClose();
        alert("Address updated successfully!");
      } catch (err) {
        console.error(err);
        alert("Internal server error.");
      } finally {
        setSaving(false);
      }
    } else {
      setSaving(false);
    }
  };

  return (
    <div className="ea-modal-content-container">
      {/* FULL NAME */}
      <div className="emc-input-wrapper">
        <label>Full Name :</label>
        <input
          type="text"
          value={fullName}
          onChange={(e) => {
            setFullName(e.target.value);
            setErrors((p) => ({ ...p, fullName: "" }));
          }}
          style={{ borderColor: errors.fullName ? "#da4f4f" : "black" }}
        />
        {errors.fullName && <span>{errors.fullName}</span>}
      </div>

      {/* CONTACT */}
      <div className="emc-input-wrapper">
        <label>Contact :</label>
        <input
          type="text"
          value={contact}
          onChange={(e) => {
            const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 11);
            setContact(onlyDigits);
            setErrors((p) => ({ ...p, contact: "" }));
          }}
          style={{ borderColor: errors.contact ? "#da4f4f" : "black" }}
        />
        {errors.contact && <span>{errors.contact}</span>}
      </div>

      {/* REGION */}
      <div className="emc-input-wrapper">
        <label>Region :</label>
        <input
          type="text"
          value={regionValue}
          onChange={(e) => {
            setProvinceValue("");
            setCityValue("");
            setBrgyValue("");
            setRegionValue(e.target.value);
            setErrors((p) => ({ ...p, region: "" }));
          }}
          onKeyDown={(e) =>
            handleKeyDown(
              e,
              filteredRegions.map((r) => r.name),
              setRegionValue,
              highlightRegion,
              setHighlightRegion,
              setIsRegionFocused
            )
          }
          onFocus={() => setIsRegionFocused(true)}
          onBlur={() => setIsRegionFocused(false)}
          style={{ borderColor: errors.region ? "#da4f4f" : "black" }}
        />
        {errors.region && <span>{errors.region}</span>}
        {isRegionFocused && filteredRegions.length > 0 && (
          <div className="emc-keyword-result">
            {filteredRegions.map((r, i) => (
              <div
                key={r.code}
                className={`keyword-result ${
                  i === highlightRegion ? "highlighted" : ""
                }`}
                onMouseDown={() => {
                  setRegionValue(r.name);
                  setProvinceValue("");
                  setCityValue("");
                  setBrgyValue("");
                  setErrors((p) => ({ ...p, region: "" }));
                }}
              >
                {r.name}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PROVINCE */}
      <div className="emc-input-wrapper">
        <label>Province/District :</label>
        <input
          type="text"
          disabled={!matchedRegion}
          value={provinceValue}
          onChange={(e) => {
            setProvinceValue(e.target.value);
            setCityValue("");
            setBrgyValue("");
            setErrors((p) => ({ ...p, province: "" }));
          }}
          onKeyDown={(e) =>
            handleKeyDown(
              e,
              filteredProvinces,
              setProvinceValue,
              highlightProvince,
              setHighlightProvince,
              setIsProvinceFocused
            )
          }
          onFocus={() => setIsProvinceFocused(true)}
          onBlur={() => setIsProvinceFocused(false)}
          style={{ borderColor: errors.province ? "#da4f4f" : "black" }}
        />
        {errors.province && <span>{errors.province}</span>}
        {isProvinceFocused && filteredProvinces?.length > 0 && (
          <div className="emc-keyword-result">
            {filteredProvinces.map((p, i) => (
              <div
                key={p}
                className={`keyword-result ${
                  i === highlightProvince ? "highlighted" : ""
                }`}
                onMouseDown={() => {
                  setProvinceValue(p);
                  setCityValue("");
                  setBrgyValue("");
                  setErrors((pErr) => ({ ...pErr, province: "" }));
                }}
              >
                {p}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CITY */}
      <div className="emc-input-wrapper">
        <label>City/Municipal :</label>
        <input
          type="text"
          disabled={!matchedProvinces}
          value={cityValue}
          onChange={(e) => {
            setCityValue(e.target.value);
            setBrgyValue("");
            setErrors((p) => ({ ...p, city: "" }));
          }}
          onKeyDown={(e) =>
            handleKeyDown(
              e,
              filteredCities,
              setCityValue,
              highlightCity,
              setHighlightCity,
              setIsCityFocused
            )
          }
          onFocus={() => setIsCityFocused(true)}
          onBlur={() => setIsCityFocused(false)}
          style={{ borderColor: errors.city ? "#da4f4f" : "black" }}
        />
        {errors.city && <span>{errors.city}</span>}
        {isCityFocused && filteredCities?.length > 0 && (
          <div className="emc-keyword-result">
            {filteredCities.map((c, i) => (
              <div
                key={c}
                className={`keyword-result ${
                  i === highlightCity ? "highlighted" : ""
                }`}
                onMouseDown={() => {
                  setCityValue(c);
                  setBrgyValue("");
                  setErrors((p) => ({ ...p, city: "" }));
                }}
              >
                {c}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* BARANGAY */}
      <div className="emc-input-wrapper">
        <label>Barangay :</label>
        <input
          type="text"
          disabled={!matchedCities}
          value={brgyValue}
          onChange={(e) => {
            setBrgyValue(e.target.value);
            setErrors((p) => ({ ...p, brgy: "" }));
          }}
          onKeyDown={(e) =>
            handleKeyDown(
              e,
              filteredBarangays,
              setBrgyValue,
              highlightBrgy,
              setHighlightBrgy,
              setIsBrgyFocused
            )
          }
          onFocus={() => setIsBrgyFocused(true)}
          onBlur={() => setIsBrgyFocused(false)}
          style={{ borderColor: errors.brgy ? "#da4f4f" : "black" }}
        />
        {errors.brgy && <span>{errors.brgy}</span>}
        {isBrgyFocused && filteredBarangays?.length > 0 && (
          <div className="emc-keyword-result">
            {filteredBarangays.map((b, i) => (
              <div
                key={b}
                className={`keyword-result ${
                  i === highlightBrgy ? "highlighted" : ""
                }`}
                onMouseDown={() => {
                  setBrgyValue(b);
                  setErrors((p) => ({ ...p, brgy: "" }));
                }}
              >
                {b}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* STREET */}
      <div className="emc-input-wrapper">
        <label>House No./ Building no / Street :</label>
        <input
          type="text"
          value={street}
          onChange={(e) => {
            setStreet(e.target.value);
            setErrors((p) => ({ ...p, street: "" }));
          }}
          style={{ borderColor: errors.street ? "#da4f4f" : "black" }}
        />
        {errors.street && <span>{errors.street}</span>}
      </div>

      {/* LANDMARK */}
      <div className="emc-input-wrapper">
        <label>Landmark (Optional):</label>
        <input
          type="text"
          value={landmark}
          onChange={(e) => setLandmark(e.target.value)}
        />
      </div>

      {/* ACTION BUTTONS */}
      <div className="emc-action-btn">
        <button className="ncm-btn-cancel" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="ncm-btn-save"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

export default EditAddress;
